// Admin passkey step-up for the user browser and impersonation (server:
// AdminAccess, Api::V1::AdminPasskeysController). A passkey check yields a
// 24-hour token kept in this browser only and sent as X-Admin-Step-Up.
// Uses the app's global $ (webpack ProvidePlugin), the same jQuery instance
// rails-ujs attaches the CSRF token to; the token is also sent explicitly.

const STORAGE_KEY = 'synaptiq.adminStepUp';
export const STEP_UP_HEADER = 'X-Admin-Step-Up';
export const STEP_UP_REQUIRED_CODE = 'ADMIN_STEP_UP_REQUIRED';

const readStored = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

// The stored token while it is unexpired, else null.
export const getStepUpToken = () => {
    const stored = readStored();
    if (!stored || !stored.token || !stored.expiresAt) return null;
    if (Date.parse(stored.expiresAt) <= Date.now()) return null;
    return stored.token;
};

export const getStepUpExpiry = () => (getStepUpToken() ? readStored().expiresAt : null);

const storeStepUp = (token, expiresAt) => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, expiresAt })); } catch (e) { /* private mode */ }
};

export const clearStepUp = () => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* private mode */ }
};

export const stepUpHeaders = () => {
    const token = getStepUpToken();
    return token ? { [STEP_UP_HEADER]: token } : {};
};

export const isStepUpRequired = (xhr) => xhr && xhr.status === 403 && xhr.responseJSON && xhr.responseJSON.code === STEP_UP_REQUIRED_CODE;

export const passkeysSupported = () => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;

// --- WebAuthn JSON <-> browser buffers --------------------------------------

const toBuffer = (b64url) => {
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = window.atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
};

const toB64url = (buffer) => {
    if (!buffer) return null;
    const bytes = new Uint8Array(buffer);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const withIds = (list) => (list || []).map((c) => ({ ...c, id: toBuffer(c.id) }));

const creationOptions = (json) => ({
    ...json,
    challenge: toBuffer(json.challenge),
    user: { ...json.user, id: toBuffer(json.user.id) },
    excludeCredentials: withIds(json.excludeCredentials),
});

const requestOptions = (json) => ({
    ...json,
    challenge: toBuffer(json.challenge),
    allowCredentials: withIds(json.allowCredentials),
});

const credentialJSON = (cred) => {
    const r = cred.response;
    const response = { clientDataJSON: toB64url(r.clientDataJSON) };
    if (r.attestationObject) {
        response.attestationObject = toB64url(r.attestationObject);
        response.transports = typeof r.getTransports === 'function' ? r.getTransports() : [];
    } else {
        response.authenticatorData = toB64url(r.authenticatorData);
        response.signature = toB64url(r.signature);
        response.userHandle = toB64url(r.userHandle);
    }
    return {
        id: cred.id,
        rawId: toB64url(cred.rawId),
        type: cred.type,
        response,
        clientExtensionResults: typeof cred.getClientExtensionResults === 'function' ? cred.getClientExtensionResults() : {},
        authenticatorAttachment: cred.authenticatorAttachment || null,
    };
};

// --- API --------------------------------------------------------------------

const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : null;
};

const call = (method, url, data) => $.ajax({
    method,
    url,
    headers: { ...stepUpHeaders(), ...(csrfToken() ? { 'X-CSRF-Token': csrfToken() } : {}) },
    contentType: 'application/json',
    dataType: 'json',
    data: data === undefined ? undefined : JSON.stringify(data),
});

export const fetchPasskeyStatus = () => call('GET', '/api/v1/admin/passkeys');

// Prompts for a passkey and stores the resulting 24-hour token.
export const verifyWithPasskey = async () => {
    const options = await call('POST', '/api/v1/admin/passkeys/authenticate/options', {});
    const credential = await navigator.credentials.get({ publicKey: requestOptions(options) });
    const result = await call('POST', '/api/v1/admin/passkeys/authenticate', { credential: credentialJSON(credential) });
    storeStepUp(result.token, result.expires_at);
    return result;
};

// Creates a passkey on this device. The first one needs the current password;
// later ones need a valid step-up (sent automatically).
export const addPasskey = async ({ label, currentPassword }) => {
    const options = await call('POST', '/api/v1/admin/passkeys/register/options', { label, current_password: currentPassword });
    const credential = await navigator.credentials.create({ publicKey: creationOptions(options) });
    return call('POST', '/api/v1/admin/passkeys/register', { credential: credentialJSON(credential) });
};

export const removePasskey = (id) => call('DELETE', `/api/v1/admin/passkeys/${id}`);

export const errorMessage = (err, fallback) => {
    if (err && err.responseJSON && err.responseJSON.error) return err.responseJSON.error;
    if (err && err.name === 'NotAllowedError') return 'The passkey prompt was cancelled or timed out.';
    if (err && err.name === 'InvalidStateError') return 'This device already has a passkey for your account.';
    return fallback;
};
